/**
 * 文件上传组件 - 支持分片上传
 */

import React, { useState, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Upload,
  X,
  File,
  FileText,
  FileVideo,
  FileImage,
  Loader2,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';
import { api } from '@/services/api';
import styles from './FileUpload.module.css';

interface FileItem {
  id: string;
  file: File;
  progress: number;
  status: 'pending' | 'uploading' | 'completed' | 'error';
  error?: string;
  fileId?: number;
  currentChunk?: number;  // 当前上传的分片
  totalChunks?: number;   // 总分片数
}

interface FileUploadProps {
  archiveId?: number;
  onComplete?: (files: { file_id: number; file_name: string }[]) => void;
  maxFileSize?: number; // GB
  allowedTypes?: string[];
}

const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB

const getFileIcon = (type: string) => {
  if (type.startsWith('video/')) return FileVideo;
  if (type.startsWith('image/')) return FileImage;
  if (type.includes('pdf') || type.includes('document') || type.includes('text')) return FileText;
  return File;
};

const formatSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

export default function FileUpload({
  archiveId,
  onComplete,
  maxFileSize = 3, // GB，默认 3GB
  allowedTypes,
}: FileUploadProps) {
  const [files, setFiles] = useState<FileItem[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const generateId = () => Math.random().toString(36).substring(2, 9);

  const validateFile = (file: File): string | null => {
    if (file.size > maxFileSize * 1024 * 1024 * 1024) {
      return `文件大小超过限制（最大 ${maxFileSize}GB）`;
    }
    if (allowedTypes && allowedTypes.length > 0) {
      const ext = file.name.split('.').pop()?.toLowerCase() || '';
      const type = file.type;
      const isAllowed = allowedTypes.some(
        (t) => type.includes(t) || ext === t.replace('.', '')
      );
      if (!isAllowed) {
        return `不支持的文件类型`;
      }
    }
    return null;
  };

  
  const uploadFile = async (fileItem: FileItem): Promise<{ file_id: number; file_name: string } | null> => {
    const { file, id } = fileItem;

    try {
      // 更新状态为上传中
      setFiles((prev) =>
        prev.map((f) => (f.id === id ? { ...f, status: 'uploading' as const, progress: 0 } : f))
      );

      // 初始化上传
      const initRes = await api.initUpload({
        file_name: file.name,
        file_size: file.size,
        file_type: file.type,
        archive_id: archiveId,
      });

      if (!initRes.data) throw new Error('初始化上传失败');

      const { upload_id, total_chunks, chunk_size } = initRes.data;

      // 更新总分片数
      setFiles((prev) =>
        prev.map((f) => (f.id === id ? { ...f, total_chunks } : f))
      );

      // 分片上传
      for (let i = 0; i < total_chunks; i++) {
        const start = i * chunk_size;
        const end = Math.min(start + chunk_size, file.size);
        const chunk = file.slice(start, end);

        // 更新当前分片信息
        setFiles((prev) =>
          prev.map((f) => (f.id === id ? { ...f, currentChunk: i + 1 } : f))
        );

        // 上传分片（内置重试逻辑）
        await api.uploadChunk(upload_id, i, chunk, 3);

        // 更新进度
        const progress = Math.round(((i + 1) / total_chunks) * 100);
        setFiles((prev) =>
          prev.map((f) => (f.id === id ? { ...f, progress } : f))
        );
      }

      // 完成上传
      let fileId: number | undefined;
      if (archiveId) {
        const completeRes = await api.completeUpload({
          upload_id,
          archive_id: archiveId,
        });
        fileId = completeRes.data?.file_id;

        setFiles((prev) =>
          prev.map((f) =>
            f.id === id
              ? {
                  ...f,
                  status: 'completed' as const,
                  fileId: fileId,
                  currentChunk: undefined,
                  totalChunks: undefined,
                }
              : f
          )
        );
      } else {
        // 如果没有 archiveId，暂时标记为完成
        setFiles((prev) =>
          prev.map((f) =>
            f.id === id ? {
              ...f,
              status: 'completed' as const,
              currentChunk: undefined,
              totalChunks: undefined,
            } : f
          )
        );
      }

      // 返回上传完成的文件信息
      if (fileId) {
        return { file_id: fileId, file_name: file.name };
      }
      return null;
    } catch (error: any) {
      console.error('上传失败:', error);
      setFiles((prev) =>
        prev.map((f) =>
          f.id === id
            ? {
                ...f,
                status: 'error' as const,
                error: error.message || '上传失败，请重试',
                currentChunk: undefined,
                totalChunks: undefined,
              }
            : f
        )
      );
      return null;
    }
  };

  // 视频文件扩展名
  const VIDEO_EXTENSIONS = ['.mp4', '.avi', '.mov', '.wmv', '.flv', '.mkv'];
  const SRT_ERROR_KEY = '缺少字幕文件(.srt)';

  const addFiles = useCallback((newFiles: FileList | File[]) => {
    const fileArray = Array.from(newFiles);
    const items: FileItem[] = [];

    for (const file of fileArray) {
      const error = validateFile(file);
      items.push({
        id: generateId(),
        file,
        progress: 0,
        status: error ? 'error' : 'pending',
        error: error || undefined,
      });
    }

    // 如果新添加的文件中包含 .srt，恢复之前因缺字幕被标记为 error 的视频文件
    const hasNewSrt = fileArray.some(f => f.name.toLowerCase().endsWith('.srt'));
    if (hasNewSrt) {
      setFiles(prev => [
        ...prev.map(f => {
          if (f.status === 'error' && f.error?.includes(SRT_ERROR_KEY)) {
            return { ...f, status: 'pending' as const, error: undefined };
          }
          return f;
        }),
        ...items,
      ]);
    } else {
      setFiles(prev => [...prev, ...items]);
    }
  }, [maxFileSize, allowedTypes]);

  const uploadAllFiles = async () => {
    const pendingFiles = files.filter((f) => f.status === 'pending');

    // 校验：视频文件必须附带字幕文件(.srt)
    const hasSrt = pendingFiles.some(f => f.file.name.toLowerCase().endsWith('.srt'));
    const videoFiles = pendingFiles.filter(f => {
      const ext = f.file.name.substring(f.file.name.lastIndexOf('.')).toLowerCase();
      return VIDEO_EXTENSIONS.includes(ext);
    });

    if (videoFiles.length > 0 && !hasSrt) {
      const videoIds = videoFiles.map(f => f.id);
      setFiles(prev => prev.map(f =>
        videoIds.includes(f.id)
          ? { ...f, status: 'error' as const, error: `${SRT_ERROR_KEY}，请使用剪映配置并导出字幕文件后再上传视频` }
          : f
      ));
      return;
    }

    const completedFilesInfo: { file_id: number; file_name: string }[] = [];

    for (const fileItem of pendingFiles) {
      const result = await uploadFile(fileItem);
      if (result) {
        completedFilesInfo.push(result);
      }
    }

    if (completedFilesInfo.length > 0 && onComplete) {
      onComplete(completedFilesInfo);
    }
  };

  const removeFile = (id: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const clearCompleted = () => {
    setFiles((prev) => prev.filter((f) => f.status !== 'completed'));
  };

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      if (e.dataTransfer.files.length > 0) {
        addFiles(e.dataTransfer.files);
      }
    },
    [addFiles]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        addFiles(e.target.files);
      }
    },
    [addFiles]
  );

  const pendingCount = files.filter((f) => f.status === 'pending').length;
  const uploadingCount = files.filter((f) => f.status === 'uploading').length;
  const completedCount = files.filter((f) => f.status === 'completed').length;

  return (
    <div className={styles.container} onClick={(e) => e.stopPropagation()}>
      {/* 拖拽区域 */}
      <div
        className={`${styles.dropzone} ${isDragging ? styles.dragging : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={(e) => {
          e.stopPropagation();
          inputRef.current?.click();
        }}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          onChange={handleInputChange}
          className={styles.input}
        />
        <Upload size={40} strokeWidth={1.5} className={styles.uploadIcon} />
        <p className={styles.dropText}>
          拖拽文件到此处，或 <span className={styles.clickText}>点击选择文件</span>
        </p>
        <p className={styles.hint}>
          单个文件最大 {maxFileSize}GB
        </p>
      </div>

      {/* 文件列表 */}
      {files.length > 0 && (
        <div className={styles.fileList}>
          <div className={styles.fileListHeader}>
            <span>
              {pendingCount > 0 && `${pendingCount} 个待上传`}
              {uploadingCount > 0 && ` · ${uploadingCount} 个上传中`}
              {completedCount > 0 && ` · ${completedCount} 个已完成`}
            </span>
            {completedCount > 0 && (
              <button type="button" className={styles.clearBtn} onClick={clearCompleted}>
                清除已完成
              </button>
            )}
          </div>

          <AnimatePresence>
            {files.map((fileItem) => {
              const Icon = getFileIcon(fileItem.file.type);
              return (
                <motion.div
                  key={fileItem.id}
                  className={styles.fileItem}
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  <div className={styles.fileInfo}>
                    <Icon size={24} className={styles.fileIcon} />
                    <div className={styles.fileDetails}>
                      <span className={styles.fileName}>{fileItem.file.name}</span>
                      <span className={styles.fileSize}>{formatSize(fileItem.file.size)}</span>
                    </div>
                  </div>

                  <div className={styles.fileStatus}>
                    {fileItem.status === 'pending' && (
                      <span className={styles.pending}>等待上传</span>
                    )}
                    {fileItem.status === 'uploading' && (
                      <div className={styles.progressWrap}>
                        <div className={styles.progressBar}>
                          <div
                            className={styles.progressFill}
                            style={{ width: `${fileItem.progress}%` }}
                          />
                        </div>
                        <span className={styles.progressText}>
                          {fileItem.currentChunk && fileItem.totalChunks
                            ? `${fileItem.currentChunk}/${fileItem.totalChunks} (${fileItem.progress}%)`
                            : `${fileItem.progress}%`}
                        </span>
                      </div>
                    )}
                    {fileItem.status === 'completed' && (
                      <CheckCircle size={20} className={styles.completedIcon} />
                    )}
                    {fileItem.status === 'error' && (
                      <div className={styles.errorWrap}>
                        <AlertCircle size={20} className={styles.errorIcon} />
                        <span className={styles.errorText}>{fileItem.error}</span>
                      </div>
                    )}
                  </div>

                  {fileItem.status !== 'uploading' && (
                    <button
                      type="button"
                      className={styles.removeBtn}
                      onClick={() => removeFile(fileItem.id)}
                    >
                      <X size={16} />
                    </button>
                  )}
                </motion.div>
              );
            })}
          </AnimatePresence>

          {/* 上传按钮 */}
          {pendingCount > 0 && (
            <motion.button
              type="button"
              className={styles.uploadBtn}
              onClick={(e) => {
                e.stopPropagation();
                uploadAllFiles();
              }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {uploadingCount > 0 ? (
                <>
                  <Loader2 size={18} className={styles.spinner} />
                  上传中...
                </>
              ) : (
                <>
                  <Upload size={18} />
                  开始上传
                </>
              )}
            </motion.button>
          )}
        </div>
      )}
    </div>
  );
}